import {useForm} from "@inertiajs/react";
import InputLabel from "@/Components/InputLabel";
import TextInput from "@/Components/TextInput";
import InputError from "@/Components/InputError";
import {PageProps, User} from "@/types";
import useTranslations from '@/hooks/useTranslations';
import {useState} from "react";

export default function ContactForm({user}: { user?: User }) {
    const { __ } = useTranslations();
    const [submitted, setSubmitted] = useState(false);
    const {data, setData, post, processing, errors, recentlySuccessful} = useForm({
        email: user?.email || '',
        name: user?.name || '',
        subject: '',
        message: '',
    })

    const subjects = [
        {key: 'technical_support', value: 'Technical Support'},
        {key: 'feature_request', value: 'Feature Request'},
        {key: 'bug_report', value: 'Bug Report'},
        {key: 'account_issues', value: 'Account Issues'},
        {key: 'billing_questions', value: 'Billing Questions'},
        {key: 'other', value: 'Other'}
    ];

    function handleSubmit(e: any) {
        e.preventDefault()
        post(route('contact'), {
            preserveScroll: true,
            onSuccess: ({props}) => {
                if(!(props as unknown as PageProps).sessionFlash.error) {
                    setSubmitted(true);
                }
            }
        })
    }

    if (submitted) {
        return (
            <div className="mt-6 p-8 text-center">
                <div className="mb-4">
                    <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    {__('contact.form.success_title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                    {__('contact.form.success_message')}
                </p>
            </div>
        );
    }

    return (
        <>
            <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                <div>
                    <InputLabel htmlFor="name" value={__('contact.form.name')}/>
                    <TextInput
                        id="name"
                        dir="auto"
                        className="mt-1 block w-full"
                        value={data.name}
                        onChange={(e) => setData('name', e.target.value)}
                        required
                        autoComplete="name"
                    />
                    <InputError className="mt-2" message={errors.name}/>
                </div>

                <div>
                    <InputLabel htmlFor="email" value={__('contact.form.email')}/>
                    <TextInput
                        id="email"
                        className="mt-1 block w-full"
                        value={data.email}
                        onChange={(e) => setData('email', e.target.value)}
                        required
                        autoComplete="email"
                    />
                    <InputError className="mt-2" message={errors.email}/>
                </div>

                <div>
                    <InputLabel htmlFor="subject" value={__('contact.form.subject')}/>
                    <select
                        id="subject"
                        className="mt-1 block w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        value={data.subject}
                        onChange={(e) => setData('subject', e.target.value)}
                        required
                    >
                        <option value="">{__('contact.form.select_subject')}</option>
                        {subjects.map((subject) => (
                            <option key={subject.key} value={subject.value}>
                                {__(`contact.form.subjects.${subject.key}`)}
                            </option>
                        ))}
                    </select>
                    <InputError className="mt-2" message={errors.subject}/>
                </div>

                <div>
                    <InputLabel htmlFor="message" value={__('contact.form.message')}/>
                    <textarea
                        id="message"
                        dir="auto"
                        rows={6}
                        className="block p-2.5 w-full text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-700 rounded-lg shadow-sm border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500"
                        placeholder={__('contact.form.message_placeholder')}
                        value={data.message}
                        onChange={(e) => setData('message', e.target.value)}
                        required
                    />
                    <InputError className="mt-2" message={errors.message}/>
                </div>

                <div className="flex items-center gap-4">
                    <button type="submit"
                            disabled={processing || recentlySuccessful}
                            className="py-3 px-5 text-sm font-medium text-center text-white rounded-lg bg-blue-700 hover:bg-blue-800 sm:w-fit focus:ring-4 focus:outline-none focus:ring-primary-300">
                        {__('contact.form.send_message')}
                    </button>
                </div>
            </form>
        </>
    )
}
